/*
 * ─── Brand configuration ────────────────────────────────────────────────────
 *
 * THE single file to edit when standing this platform up for a new brokerage.
 * Everything user-visible — the form, the ops console, the broker portal,
 * applicant emails and the printable summaries — reads its brand identity
 * from here at build time (the server imports it too, for email copy and
 * TOTP issuer names).
 *
 * This deployment is Broker Kong. Identity and theme below are real (see
 * docs/BRAND_GUIDE.md for the palette and the rules behind it).
 *
 * STILL PLACEHOLDERS — do not go live until these carry Broker Kong's real,
 * counsel-approved values: `website`, `phone`, `emails`, `address` and every
 * field under `legal` (entity names, ABN/ACN, Australian Credit Licence).
 * The legal pages under public/ are placeholder documents too — your own
 * counsel supplies those (see the notes at the top of each file).
 * `npm run check:brand` lists whatever is still unset.
 *
 * Deployment-specific values (base URLs, email sending identity, bucket
 * names, lender credentials) stay in environment variables — see
 * .env.example. This file is for the parts of the brand that are baked into
 * the built bundles.
 */
export default {
  /* Trading name shown across the form, console, broker portal and emails. */
  name: "Broker Kong",

  /* One-liner under the logo on the form's welcome rail. */
  tagline: "More leads. Higher levels.",

  /* Public website + contact identity used in footers and email copy. */
  website: "https://www.example.com.au",
  phone: "1300 000 000",
  emails: {
    hello: "hello@example.com.au",
    complaints: "complaints@example.com.au",
  },
  address: "Level 1, 1 Example Street, Brisbane QLD 4000",

  /*
   * Legal entities. Consumer lending (NCCP-regulated) and commercial /
   * asset finance are often written by DIFFERENT entities — the form and the
   * printable summary show the entity that matches the product so the
   * applicant always sees who they are dealing with. Replace with your
   * counsel-approved details; leave `commercial` null if one entity writes
   * everything.
   */
  legal: {
    consumer: {
      entity: "Acme Finance Pty Ltd",
      abn: "00 000 000 000",
      acl: "000000", // Australian Credit Licence
    },
    commercial: {
      entity: "Acme Commercial Pty Ltd",
      acn: "000 000 000",
      abn: "00 000 000 000",
    },
  },

  /*
   * Application reference prefix: display references shown to applicants
   * (e.g. BK-123456) and the default bank-statements slug prefix
   * (overridable at runtime with BANK_STATEMENTS_SLUG_PREFIX).
   */
  referencePrefix: "BK",

  /*
   * What the UI calls the downstream lender the platform submits to.
   * With the Nimo adapter configured you would set label: "Nimo"; the
   * generic default keeps the console meaningful with no lender connected.
   * (Which adapter actually runs is the LENDER_ADAPTER env var — see
   * server/lib/lender.js.)
   */
  lender: {
    label: "the lender",   // mid-sentence: "submitted to the lender"
    shortLabel: "Lender",  // headings, tabs, buttons
  },

  /*
   * Embed identity: the global name the <script> bundle registers
   * (window.<embedGlobal>.mount(...)) and the base file name the library
   * build emits (dist/embed/<embedFileBase>.iife.js / .es.js / .css).
   * Rebuild after changing these, and update any embed snippets you have
   * shared with partners.
   */
  embedGlobal: "ApplicationForm",
  embedFileBase: "application-form",

  /*
   * TOTP issuer names shown in authenticator apps for console and broker
   * sign-in. null = derived from `name` ("Broker Kong Ops" / "… Brokers").
   */
  totp: { opsIssuer: null, brokerIssuer: null },

  /*
   * Theme — the colours and typefaces every surface (form, console, broker
   * portal, applicant link pages) is built on. Leave a key out (or leave the
   * value as shipped) and the surface keeps its shipped default; change a
   * value and it is injected as a CSS-variable override at load, with the
   * in-between ramp shades derived automatically. The five accent values
   * belong together: pick your primary, a hover and an active shade, and two
   * light tints of it.
   */
  theme: {
    /* Broker Kong — see docs/BRAND_GUIDE.md for the full palette, the
       contrast measurements behind these choices and the usage rules. */
    accent: "#D63F33",       // Kong Red — primary action colour (buttons, links, progress)
    accentDark: "#C53A2F",   // hover
    accentDarker: "#B8362C", // active / pressed
    accentTint: "#F9E4E2",   // light accent surface (selected rows, chips)
    accentTint2: "#FCF2F1",  // lightest accent surface
    ink: "#061D2F",          // Kong Navy — dark brand surface (logo tile, tooltips)
    surface: "#FBF6EA",      // Parchment — the warm page ground behind the cards
    fontDisplay: "Archivo",  // headings + UI chrome (echoes the athletic wordmark)
    fontBody: "Inter",       // body copy (the form)
    /* Google Fonts family specs loaded when the fonts above are not the
       shipped ones (set to [] if you self-host your fonts). */
    googleFonts: [
      "Archivo:wght@500;600;700;800",
      "Inter:wght@400;500;600;700;800",
    ],
  },
};
